import React, { useState, useCallback, useEffect } from 'react';
import { Search, Mic, X as ClearIcon } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import useDebounce from '../../../../hooks/useDebounce';
import { RootState } from '../../../../reducers';
import { screenAppAPIClient } from '../../../../lib/baseAxios';
import { FileType } from '../../../../types';

interface FileSelectorProps {
  selectedFiles: string[];
  onFileSelect: (file: FileType) => void;
}

interface SearchResult extends FileType {
  fileId?: string;
  fileName?: string;
  recordingId?: string;
}

const FileSelector: React.FC<FileSelectorProps> = ({
  selectedFiles,
  onFileSelect,
}) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalFiles, setTotalFiles] = useState(0);
  const [hasMoreFiles, setHasMoreFiles] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(false);
  const [shouldLoadMore, setShouldLoadMore] = useState(false);
  const [preloadedPages, setPreloadedPages] = useState<Set<number>>(new Set());

  const ITEMS_PER_PAGE = 20;
  const debouncedShouldLoadMore = useDebounce(shouldLoadMore, 50);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const teamState = useSelector((state: RootState) => state.team.selectedTeam);
  const teamId = teamState?.teamId;

  // Helper function to render file thumbnail
  const renderThumbnail = (file: SearchResult, fileName: string) => {
    // Audio-only file
    if (file.streams && !file.streams.includes('video')) {
      return (
        <div className="w-12 h-8 bg-gray-100 dark:bg-gray-800 rounded flex-shrink-0 flex items-center justify-center">
          <Mic className="w-4 h-4 text-gray-400 dark:text-gray-600 opacity-50" />
        </div>
      );
    }

    // Video file with thumbnail
    if (file.thumbUrl) {
      return (
        <img
          src={file.thumbUrl}
          alt={fileName}
          className="w-12 h-8 object-cover rounded flex-shrink-0 bg-gray-100 dark:bg-gray-800"
        />
      );
    }

    // Video file without thumbnail
    return (
      <div className="w-12 h-8 bg-gray-100 dark:bg-gray-800 rounded flex-shrink-0" />
    );
  };

  const fetchFiles = useCallback(
    async (page: number, append: boolean = false, search?: string) => {
      if (!teamId) return;

      try {
        if (!append) {
          setLoading(true);
        } else {
          setIsLoadingMore(true);
        }
        setError(null);

        const queryString = search || searchQuery || '';

        // Use assisted search if there's a search query, otherwise use regular endpoint
        if (queryString.trim()) {
          // Use assisted search endpoint
          console.log(
            '[FileSelector] Using assisted search for query:',
            queryString,
          );
          const response = await (screenAppAPIClient as any).api.post(
            `/v2/files/assistant-search/${teamId}`,
            {
              query: queryString,
              includeThumbnails: false,
              limit: ITEMS_PER_PAGE,
            },
          );

          if (response.data.success && response.data.data) {
            const newFiles = response.data.data.searchResults || [];
            console.log(
              '[FileSelector] Assisted search returned',
              newFiles.length,
              'files',
            );

            // For pagination in assisted search, we don't have total count
            // So we just check if we got a full page to determine if there's more
            if (append) {
              setFiles((prev) => [...prev, ...newFiles]);
            } else {
              setFiles(newFiles);
            }

            setTotalFiles(newFiles.length); // We don't have real total from assisted search
            setCurrentPage(page);
            setHasMoreFiles(newFiles.length === ITEMS_PER_PAGE); // Has more if we got a full page
            setPreloadedPages((prev) => new Set([...prev, page]));
          } else {
            setError('Failed to load files');
          }
        } else {
          // Use regular file listing endpoint when no search query
          console.log(
            '[FileSelector] Using regular file listing (no search query)',
          );
          const response = await (screenAppAPIClient as any).api.get(
            `/v2/files`,
            {
              params: {
                teamId,
                sortBy: 'createdAt',
                sortOrder: 'desc',
                page,
                limit: ITEMS_PER_PAGE,
              },
            },
          );

          if (response.data.success && response.data.data) {
            const newFiles = response.data.data.fileSystem || [];
            const total =
              response.data.data.pagination?.total || newFiles.length;

            if (append) {
              setFiles((prev) => [...prev, ...newFiles]);
            } else {
              setFiles(newFiles);
            }

            setTotalFiles(total);
            setCurrentPage(page);
            setHasMoreFiles(
              newFiles.length === ITEMS_PER_PAGE &&
                files.length + newFiles.length < total,
            );
            setPreloadedPages((prev) => new Set([...prev, page]));
          } else {
            setError('Failed to load files');
          }
        }
      } catch (err: any) {
        console.error('Error fetching files:', err);
        setError('Failed to load files');
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
      }
    },
    [teamId, ITEMS_PER_PAGE, files.length, searchQuery],
  );

  // Initial fetch
  useEffect(() => {
    if (!teamId) return;

    // Reset state
    setFiles([]);
    setCurrentPage(1);
    setTotalFiles(0);
    setHasMoreFiles(true);
    setPreloadedPages(new Set());
    setSearchQuery('');

    const initialFetch = async () => {
      await fetchFiles(1, false);
    };
    initialFetch();
  }, [teamId]);

  // Refetch when debounced search query changes
  useEffect(() => {
    if (!teamId) return;

    // Don't trigger on initial render
    if (debouncedSearchQuery === '' && files.length === 0) {
      return;
    }

    // Reset pagination state when search changes
    setCurrentPage(1);
    setTotalFiles(0);
    setHasMoreFiles(true);
    setPreloadedPages(new Set());

    fetchFiles(1, false, debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  // Scroll handling for infinite loading
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const { scrollHeight, scrollTop, clientHeight } = target;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // Preload when user is 2 viewport heights away from bottom
      const preloadThreshold = window.innerHeight * 2;
      // Show skeleton only when very close to bottom
      const skeletonThreshold = window.innerHeight * 0.5;

      // Update near-bottom state for skeleton display
      setIsNearBottom(distanceFromBottom < skeletonThreshold);

      if (
        distanceFromBottom < preloadThreshold &&
        !loading &&
        !isLoadingMore &&
        hasMoreFiles
      ) {
        const nextPage = currentPage + 1;
        const maxPages = Math.ceil(totalFiles / ITEMS_PER_PAGE);

        // Prevent duplicate page requests
        if (nextPage > maxPages || preloadedPages.has(nextPage)) {
          return;
        }

        // Trigger the debounced load
        setShouldLoadMore(true);
      } else {
        setShouldLoadMore(false);
      }
    },
    [
      loading,
      isLoadingMore,
      hasMoreFiles,
      currentPage,
      totalFiles,
      ITEMS_PER_PAGE,
      preloadedPages,
    ],
  );

  // Effect to handle the actual API call when debounced trigger fires
  useEffect(() => {
    if (debouncedShouldLoadMore && !isLoadingMore && hasMoreFiles) {
      const nextPage = currentPage + 1;
      const maxPages = Math.ceil(totalFiles / ITEMS_PER_PAGE);

      if (nextPage <= maxPages && !preloadedPages.has(nextPage)) {
        fetchFiles(nextPage, true);
      }
    }
  }, [
    debouncedShouldLoadMore,
    isLoadingMore,
    hasMoreFiles,
    currentPage,
    totalFiles,
    ITEMS_PER_PAGE,
    preloadedPages,
    fetchFiles,
  ]);

  const handleClearSearch = () => {
    setSearchQuery('');
    // Reload files without search term
    if (teamId) {
      setCurrentPage(1);
      setTotalFiles(0);
      setHasMoreFiles(true);
      setPreloadedPages(new Set());
      fetchFiles(1, false, '');
    }
  };

  return (
    <div className="w-96 max-h-96 overflow-hidden bg-popover border border-border rounded-md shadow-md">
      {/* Search bar - minimal style like SearchDialog */}
      <div className="flex items-center gap-3 px-3 py-3 border-b border-gray-200 dark:border-gray-700">
        <Search className="h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder={t(
            'drawer.multiAskAI.searchRecordings',
            'Search recordings...',
          )}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 bg-transparent outline-none text-base"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="opacity-70 transition-opacity hover:opacity-100"
          >
            <ClearIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* File list in dropdown */}
      <ScrollArea className="h-64" onScrollCapture={handleScroll}>
        {(() => {
          if (loading) {
            return (
              <div>
                <div>
                  {Array.from({ length: 8 }, (_, i) => (
                    <div key={i} className="flex gap-3 px-3 py-2.5 mb-0.5">
                      <Skeleton className="w-12 h-8 rounded flex-shrink-0" />
                      <div className="flex-1 space-y-1">
                        <Skeleton className="h-4 w-full max-w-[70%]" />
                        <Skeleton className="h-3 w-full max-w-[40%]" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          if (error) {
            return (
              <div className="flex items-center justify-center h-full p-8">
                <div className="text-center space-y-1">
                  <p className="text-sm font-medium text-destructive">
                    {error}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Please try again
                  </p>
                </div>
              </div>
            );
          }

          if (files.length === 0) {
            return (
              <div className="flex items-center justify-center h-full p-8">
                <p className="text-sm text-muted-foreground">
                  {searchQuery
                    ? 'No recordings found'
                    : 'No recordings available'}
                </p>
              </div>
            );
          }

          return (
            <div>
              <div>
                {files.map((file) => {
                  const fileId = file.fileId || file._id;
                  const fileName = file.fileName || file.name || 'Untitled';
                  const isSelected = selectedFiles.includes(fileId);
                  const isDisabled = !file.textData?.transcriptUrl;

                  return (
                    <button
                      key={fileId}
                      onClick={() => !isDisabled && onFileSelect(file)}
                      onKeyDown={(e) => {
                        if (
                          (e.key === 'Enter' || e.key === ' ') &&
                          !isDisabled
                        ) {
                          e.preventDefault();
                          onFileSelect(file);
                        }
                      }}
                      type="button"
                      className={cn(
                        'w-full text-left group flex items-center gap-3 px-3 py-2.5 mb-0.5 cursor-pointer transition-colors relative',
                        !isDisabled &&
                          'hover:bg-gray-50 dark:hover:bg-gray-800',
                        isDisabled && 'cursor-not-allowed opacity-60',
                      )}
                    >
                      {/* Thumbnail */}
                      <div className="relative">
                        {renderThumbnail(file, fileName)}
                        {/* Checkbox overlay on hover */}
                        <div
                          className={cn(
                            'absolute inset-0 flex items-center justify-center bg-black/60 rounded-md transition-opacity',
                            isSelected
                              ? 'opacity-100'
                              : 'opacity-0 group-hover:opacity-100',
                          )}
                        >
                          <Checkbox
                            checked={isSelected}
                            disabled={isDisabled}
                            onCheckedChange={() =>
                              !isDisabled && onFileSelect(file)
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="data-[state=checked]:bg-primary data-[state=checked]:border-primary border-white"
                          />
                        </div>
                      </div>

                      {/* File info */}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{fileName}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Loading skeleton for pagination */}
              {isLoadingMore && isNearBottom && hasMoreFiles && (
                <div>
                  {Array.from({ length: 4 }, (_, i) => (
                    <div
                      key={`skeleton-${i}`}
                      className="flex gap-3 px-3 py-2.5 mb-0.5"
                    >
                      <Skeleton className="w-12 h-8 rounded flex-shrink-0" />
                      <div className="flex-1 space-y-1">
                        <Skeleton className="h-4 w-full max-w-[70%]" />
                        <Skeleton className="h-3 w-full max-w-[40%]" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </ScrollArea>
    </div>
  );
};

export default FileSelector;
